/**
 * Google Maps Provider
 *
 * Loads Google Maps JavaScript API once at the application level
 * to avoid multiple script injections and improve performance.
 *
 * USAGE:
 * Wrap your application with this provider in the root layout.
 *
 * FEATURES:
 * - Loads script once on app initialization
 * - Provides loading state to child components
 * - Handles errors gracefully
 * - Prevents duplicate script loading
 */

'use client';

import { useLoadScript } from '@react-google-maps/api';
import { createContext, useContext, type ReactNode } from 'react';

interface GoogleMapsContextValue {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextValue>({
  isLoaded: false,
  loadError: undefined,
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

interface GoogleMapsProviderProps {
  children: ReactNode;
}

/**
 * Libraries to load from Google Maps API
 * Add more as needed (places, drawing, geometry, etc.)
 */
const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places'];

export const GoogleMapsProvider = ({ children }: GoogleMapsProviderProps) => {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  // Validate API key presence on mount
  if (typeof window !== 'undefined' && !apiKey) {
    console.warn('⚠️ Google Maps API key is not configured');
    console.warn('Set NEXT_PUBLIC_GOOGLE_MAPS_API_KEY in .env.local');
    console.warn('See GOOGLE_MAPS_SETUP.md for setup instructions');
  }

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || '',
    libraries,
    // Prevent multiple script loads
    id: 'google-maps-script',
  });

  // Log errors for debugging
  if (loadError) {
    console.error('Error loading Google Maps script:', {
      name: loadError.name,
      message: loadError.message,
      stack: loadError.stack,
      apiKey: apiKey ? `${apiKey.substring(0, 10)}...` : 'NOT_SET',
    });

    // Additional helpful messages
    if (!apiKey) {
      console.error('MISSING API KEY: Please set NEXT_PUBLIC_GOOGLE_MAPS_API_KEY in .env.local');
    } else {
      console.error('API KEY TROUBLESHOOTING:');
      console.error('1. Verify Maps JavaScript API is enabled in Google Cloud Console');
      console.error('2. Check billing is set up (required for Google Maps)');
      console.error('3. Verify API restrictions allow this domain');
      console.error('4. For localhost, ensure no HTTP referrer restrictions');
      console.error('See GOOGLE_MAPS_SETUP.md for detailed instructions');
    }
  }

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};
